import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useGroupsApi } from "./useGroupsHook";
import { cachedDataItemSet } from "../redux/actions/cachedDataActions";
import { CACHED_DATA_REDUCER_TYPES } from "../redux/reducers/cachedDataReducer";

const KEY = CACHED_DATA_REDUCER_TYPES.USER_GROUPS;

/**
 * Several components ask this at once — the header nav (Users menu, Sizers
 * menu) and the screens those menus open. A per-component `useState` would
 * fetch once per caller and, worse, could disagree. The answer lives in the
 * shared `cachedData` slice instead, so every caller reads one value and all
 * re-read when it's cleared.
 */
const inFlight = { promise: null, email: null };

/**
 * The org groups the logged-in user belongs to, as lowercased names, from
 * `GET /groups/for-user/{email}`. The building block for every group gate
 * (`useIsInUserActivityGroup`, `useCanSeeSizers`) — a gate decides who skips
 * the lookup (super admins, etc.) and which group name it wants.
 *
 * `skip` — don't fetch (the caller already knows the answer). `names` then
 * stays whatever is cached, possibly null.
 *
 * **Kept live.** `useGroupsHook` clears the cached list on every membership
 * mutation, so a gate built on this updates on the spot rather than at the
 * next page load.
 *
 * An errored lookup resolves to `[]` — gates built on this fail CLOSED.
 *
 * @returns {{ names: string[] | null, loading: boolean }}
 */
export const useUserGroupNames = ({ skip = false } = {}) => {
  const dispatch = useDispatch();
  const email = useSelector((state) => state.user?.emailAddress);
  const cached = useSelector((state) => state.cachedData?.[KEY]);
  const { groupsForUser } = useGroupsApi();

  useEffect(() => {
    let active = true;
    if (skip || !email || Array.isArray(cached)) return undefined;

    // One request per (email, invalidation) no matter how many components ask.
    if (!inFlight.promise || inFlight.email !== email) {
      inFlight.email = email;
      inFlight.promise = groupsForUser(email)
        .then((groups) =>
          (groups || [])
            .map((g) => String(g?.name || "").trim().toLowerCase())
            .filter(Boolean),
        )
        .catch(() => [])
        .finally(() => {
          inFlight.promise = null;
        });
    }

    inFlight.promise.then((names) => {
      if (active) dispatch(cachedDataItemSet(KEY, names));
    });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, email, cached]);

  return {
    names: Array.isArray(cached) ? cached : null,
    loading: !Array.isArray(cached),
  };
};

/** Case-insensitive membership test against `useUserGroupNames().names`. */
export const hasGroup = (names, groupName) =>
  Array.isArray(names) && names.includes(String(groupName).toLowerCase());

export default useUserGroupNames;
